/** @jsxImportSource @emotion/react */

import { useMemo, useState } from 'react'
import { Typography } from '@mui/material'
import { createSearchParams, useSearchParams, Link } from 'react-router-dom'
import Button from '@mui/material/Button'
import TabsNavigation from 'components/TabsNavigation/TabsNavigation'
import { TabsItem } from 'types/tabsItem'
import { cmsRoutes, RouteValue, useLink } from 'routes/cmsRoutes'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { PlatformSlug } from 'constants/consts'
import NotFound from 'components/NotFound'
import * as classes from './styles'
import { List as MembershipsList } from './components/Memberships'
import { List as AdditionalTestsList } from './AdditionalTests'
import { List as PauseTokensList } from './PauseTokens'
import { QUERY_PARAM, getTabs, MEMBERSHIPS_TAB, EXTENSIONS_TAB, ADDITIONAL_TAB, PAUSE_TOKENS_TAB } from './consts'
import { isProductsAvailable } from './platformConfig'

type Props = {
  platform: Platform
}

export const Products = withPlatformCheck(({ platform }: Props) => {
  const [searchParams] = useSearchParams()
  const { navigate, getLink } = useLink()

  const tabs = useMemo(() => getTabs(platform.slug as PlatformSlug), [platform.slug])
  const [activeTab, setActiveTab] = useState<TabsItem>(() => {
    const tabId = searchParams.get(QUERY_PARAM)

    return tabs.find(({ id }) => id === tabId) || tabs[0]
  })

  const handleChangeTab = (activeTab: TabsItem) => {
    navigate(location.pathname as RouteValue, undefined, {
      searchParams: createSearchParams({ [QUERY_PARAM]: activeTab.id }),
    })
    setActiveTab(activeTab)
  }

  const renderTab = () => {
    if (activeTab.id === MEMBERSHIPS_TAB.id) {
      return <MembershipsList key="memberships" platform={platform} isExtension={false} />
    }

    if (activeTab.id === EXTENSIONS_TAB.id) {
      return <MembershipsList key="extensions" platform={platform} isExtension />
    }

    if (activeTab.id === ADDITIONAL_TAB.id) {
      return <AdditionalTestsList key="additional-tests" platform={platform} />
    }

    if (activeTab.id === PAUSE_TOKENS_TAB.id) {
      return <PauseTokensList key="pause-tokens" platform={platform} />
    }

    return null
  }

  if (!isProductsAvailable(platform.slug as PlatformSlug) || !tabs.length || !activeTab) {
    return <NotFound />
  }

  return (
    <section css={classes.container}>
      <div css={classes.headerWrapper}>
        <Typography css={classes.header} variant="title3">
          Products
        </Typography>
        {activeTab.id === EXTENSIONS_TAB.id && (
          <Link to={getLink(cmsRoutes.PRODUCTS_EXTENSION_CREATE)}>
            <Button color="blue" variant="contained">
              Add Extension
            </Button>
          </Link>
        )}
      </div>

      <TabsNavigation css={classes.tabs} tabs={tabs} value={activeTab} onChange={handleChangeTab} />

      {renderTab()}
    </section>
  )
})
